import { useT, useUiStore } from '@/store';
import { formatDate } from '@/lib/format';

/**
 * "(Archyvuota)" — the marker that says a name in a historical row belongs to
 * something no longer in the active fleet.
 *
 * It exists because the orders/reporting queries join quarries and sites
 * WITHOUT filtering `active`, which is what keeps a two-year-old order
 * resolving to a quarry name at all. The cost of that (correct) choice is
 * that an archived quarry and a live one render identically, so a dispatcher
 * reading a legacy waybill cannot tell that its loading point is gone until
 * they go looking for it in Duomenys and find nothing.
 *
 * `deletedAt` is optional on purpose. Rows archived before the deleted_at
 * column existed carry NULL, and this app has no way to know when those were
 * archived — so the badge renders bare rather than inventing a date. Both
 * shapes say the same true thing; only one of them says when.
 */
export function ArchivedBadge({ deletedAt }) {
  const t = useT();
  const lang = useUiStore((s) => s.lang);
  const on = deletedAt ? t('common.archivedOn', { date: formatDate(deletedAt, lang) }) : null;

  return (
    <span
      className="ml-1.5 whitespace-nowrap rounded-full bg-white/8 px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-[0.02em] text-dim"
      title={on ?? t('common.archived')}
    >
      ({t('common.archived')})
    </span>
  );
}
